/**
 * Phase F — Referral / Share & Earn API helpers.
 *
 * Thin wrappers around `apiClient` so the page components don't have to
 * know endpoint URLs. All endpoints live under `/api/referrals` for the
 * user-facing surface and `/api/admin/referrals` for the admin one.
 */
import { apiClient } from "./client";

// ----- User-facing -----
export async function getReferralSummary() {
  const { data } = await apiClient.get("/referrals/me");
  return data;
}

// Sprint L P1 #6 / Sprint M Item 3 — live-validate a desired custom slug.
// Returns `{ available, normalized, reason?, message? }`.
export async function checkReferralAvailability(code) {
  const { data } = await apiClient.get("/referrals/check-availability", {
    params: { code },
  });
  return data;
}

// Sprint L P1 #6 / Sprint M Item 3 — claim/replace your personal slug.
export async function setMyReferralCode(code) {
  const { data } = await apiClient.patch("/referrals/my-code", { code });
  return data;
}

export async function listMyPayoutRequests() {
  const { data } = await apiClient.get("/referrals/payout-requests/me");
  return data.items || [];
}

export async function createPayoutRequest({ iban, account_holder_name, note }) {
  const { data } = await apiClient.post("/referrals/payout-request", {
    iban,
    account_holder_name,
    note: note || null,
  });
  return data;
}

// ----- Admin -----
export async function adminListReferralUsers(params = {}) {
  const { data } = await apiClient.get("/admin/referrals/users", { params });
  return data;
}

export async function adminGetReferralUserDetail(ulid) {
  const { data } = await apiClient.get(`/admin/referrals/users/${ulid}`);
  return data;
}

export async function adminListPayouts(params = {}) {
  const { data } = await apiClient.get("/admin/referrals/payouts", { params });
  return data;
}

export async function adminGetPayoutDetail(ulid) {
  const { data } = await apiClient.get(`/admin/referrals/payouts/${ulid}`);
  return data;
}

export async function adminApprovePayout(ulid, body = {}) {
  const { data } = await apiClient.post(
    `/admin/referrals/payouts/${ulid}/approve`,
    body,
  );
  return data;
}

export async function adminMarkPayoutPaid(ulid, body = {}) {
  const { data } = await apiClient.post(
    `/admin/referrals/payouts/${ulid}/mark-paid`,
    body,
  );
  return data;
}

export async function adminRejectPayout(ulid, body = {}) {
  const { data } = await apiClient.post(
    `/admin/referrals/payouts/${ulid}/reject`,
    body,
  );
  return data;
}

// ----- Admin · Vanity (custom) Codes — Phase V1.5 -----
//
// Admin-managed marketing codes (e.g. KOSMO, VIENNA-2026). Unlike the
// auto-generated user codes, these are NOT tied to a user_ulid; they
// only bump a marketing counter on registration. CRUD endpoints live
// under `/api/admin/referrals/custom-codes`.
export async function adminListCustomCodes(params = {}) {
  const { data } = await apiClient.get("/admin/referrals/custom-codes", { params });
  return data;
}

export async function adminCreateCustomCode({ code, owner_name, owner_email, note }) {
  const { data } = await apiClient.post("/admin/referrals/custom-codes", {
    code,
    owner_name,
    owner_email: owner_email || null,
    note: note || null,
  });
  return data;
}

export async function adminPatchCustomCode(code, patch) {
  const { data } = await apiClient.patch(
    `/admin/referrals/custom-codes/${encodeURIComponent(code)}`,
    patch,
  );
  return data;
}

export async function adminDisableCustomCode(code) {
  const { data } = await apiClient.delete(
    `/admin/referrals/custom-codes/${encodeURIComponent(code)}`,
  );
  return data;
}

export async function adminHardDeleteCustomCode(code) {
  const { data } = await apiClient.delete(
    `/admin/referrals/custom-codes/${encodeURIComponent(code)}/hard`,
  );
  return data;
}

export async function adminGetMagicLink(code, qr_size = 256) {
  const { data } = await apiClient.get(
    `/admin/referrals/custom-codes/${encodeURIComponent(code)}/magic-link`,
    { params: { qr_size } },
  );
  return data;
}
